import { type ProjectAttributes } from './generated/ProjectAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { HasCustomFieldValues } from './concerns/HasCustomFieldValues';
import { HasWipLimits } from './concerns/HasWipLimits';
import User from './User';
import ColorAttribute, {
  type IColorAttribute,
} from './concerns/ColorAttribute';
import { TeamFlavorEnum } from '../Enums';

interface Project
  extends ProjectAttributes,
    HasWipLimits,
    HasCustomFieldValues,
    IColorAttribute {
  blankUser: User;
}

@model
@HasWipLimits
@HasCustomFieldValues
@ColorAttribute()
class Project extends ApplicationModel {
  // Start auto-generated
  static typename: 'Project' = 'Project';
  get typename(): 'Project' {
    return 'Project';
  }

  static internalTypename: 'Project' = 'Project';
  get internalTypename(): 'Project' {
    return 'Project';
  }

  static terminologyName = 'Product';

  static inputType = 'ProjectAttributes!';

  static filterType = 'ProjectFilters';
  static filters = { id: '[ID!]', teams: 'Boolean' };

  static fields = {
    backlogBookmarkId: attr({ readable: true }),
    backlogManagementEnabled: attr({ readable: true }),
    canCreatePages: attr({ readable: true }),
    childrenCount: attr({ readable: true }),
    color: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    defaultCapacityUnits: attr({ readable: true }),
    developTeamsCanImportIdeas: attr({ readable: true }),
    developTeamsCount: attr({ readable: true }),
    discoveryStudiesCount: attr({ readable: true }),
    drillInUrl: attr({
      args: {
        publishableId: 'ID',
        publishableType: 'String',
        viewSnapshotId: 'ID',
        dashboardId: 'ID',
        exportType: 'String',
        bookmarkId: 'ID',
      },
      readable: true,
    }),
    epicWorkflowId: attr({ readable: true }),
    epicsEnabled: attr({ readable: true }),
    featureWorkflowId: attr({ readable: true }),
    goalsCount: attr({ readable: true }),
    id: attr({ readable: true }),
    ideaCategoriesCount: attr({ readable: true }),
    ideaPortalsCount: attr({ readable: true }),
    ideaWorkflowId: attr({ readable: true }),
    ideasCount: attr({ readable: true }),
    initiativeWorkflowId: attr({ readable: true }),
    initiativesCount: attr({ readable: true }),
    isProductLine: attr({ readable: true }),
    isTeam: attr({ readable: true }),
    iterationsEnabled: attr({ readable: true }),
    name: attr({ writable: true, readable: true }),
    parentId: attr({ readable: true }),
    path: attr({ readable: true }),
    programIncrementsCount: attr({ readable: true }),
    projectsCount: attr({ readable: true }),
    referencePrefix: attr({ readable: true }),
    releaseWorkflowId: attr({ readable: true }),
    releasesCount: attr({ readable: true }),
    requirementWorkflowId: attr({ readable: true }),
    strategicImperativeWorkflowId: attr({ readable: true }),
    teamFlavor: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    currentIteration: attr.belongsTo({ readable: true, typename: 'Iteration' }),
    defaultRelease: attr.belongsTo({ readable: true, typename: 'Release' }),
    defaultUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({ readable: true, typename: 'Note' }),
    epicWorkflow: attr.belongsTo({ readable: true, typename: 'Workflow' }),
    featureWorkflow: attr.belongsTo({ readable: true, typename: 'Workflow' }),
    ideaWorkflow: attr.belongsTo({ readable: true, typename: 'Workflow' }),
    initiativeWorkflow: attr.belongsTo({
      readable: true,
      typename: 'Workflow',
    }),
    parent: attr.belongsTo({ readable: true, typename: 'Project' }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    projectConfiguration: attr.belongsTo({
      writeShape: ['cardLayout'],
      readable: true,
      typename: 'ProjectConfiguration',
    }),
    projectPermissions: attr.belongsTo({
      readable: true,
      typename: 'ProjectPermissions',
    }),
    projectTerminology: attr.belongsTo({
      readable: true,
      typename: 'Terminology',
    }),
    releaseWorkflow: attr.belongsTo({ readable: true, typename: 'Workflow' }),
    requirementWorkflow: attr.belongsTo({
      readable: true,
      typename: 'Workflow',
    }),
    strategicImperativeWorkflow: attr.belongsTo({
      readable: true,
      typename: 'Workflow',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    workflowBoardBookmark: attr.belongsTo({
      readable: true,
      typename: 'BookmarksWorkflowBoard',
    }),
    children: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    customFieldsRelatedToTeam: attr.hasMany({
      args: { types: '[CustomFieldDefinitionTypeEnum!]' },
      readable: true,
    }),
    developTeams: attr.hasMany({ readable: true }),
    discoveryStudies: attr.hasMany({ readable: true }),
    extensionFields: attr.hasMany({
      args: { filters: 'ExtensionFieldFilters' },
      readable: true,
    }),
    goals: attr.hasMany({ readable: true }),
    ideaCategories: attr.hasMany({ readable: true }),
    ideaPortals: attr.hasMany({ readable: true }),
    ideas: attr.hasMany({ readable: true }),
    initiatives: attr.hasMany({ readable: true }),
    programIncrements: attr.hasMany({ readable: true }),
    projects: attr.hasMany({ readable: true }),
    releases: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    teamUsers: attr.hasMany({ readable: true }),
    users: attr.hasMany({ readable: true }),
    wipLimits: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  resetAttributes(attributes, errors = {}) {
    super.resetAttributes(attributes, errors);
    this.blankUser = User.blank({
      name: `Default (${this.defaultUser?.name || 'Unassigned'})`,
    });
  }

  get isDevelopTeam() {
    return this.teamFlavor === TeamFlavorEnum.DEVELOP.value;
  }

  get isTeamworkTeam() {
    return this.teamFlavor === TeamFlavorEnum.TEAMWORK.value;
  }
}

export default Project;
